import React from 'react';
import { Panel } from 'src/ui/Panel';
import { Badge } from 'src/ui/Badge';
import { Button } from 'src/ui/Button';
import {
  JudgeVerdict,
  Simulation,
  SimulationMetrics,
  SimulationStatus,
  SimulationStopReason,
} from 'src/store/simulationApi';

const STATUS: Record<SimulationStatus, [string, string]> = {
  running: ['blue', 'Running'],
  done: ['green', 'Done'],
  stopped: ['gray', 'Stopped'],
  failed: ['red', 'Failed'],
};

const STOP_REASON: Record<SimulationStopReason, string> = {
  goal: 'goal reached',
  cap: 'turn cap reached',
  stopped: 'stopped by you',
  failed_turn: "Hong's turn failed",
  persona_repeat: 'the simulated user repeated itself',
  error: 'error',
};

const GOAL: Record<Simulation['goal'], string> = {
  first_card: 'first card',
  half: '50% profile',
  none: 'turn cap',
};

const JUDGE_LABELS: [keyof Omit<JudgeVerdict, 'issues'>, string][] = [
  ['overall', 'Overall'],
  ['naturalness', 'Natural'],
  ['noRepetition', 'No repeats'],
  ['listens', 'Listens'],
  ['pacing', 'Pacing'],
  ['languageConsistency', 'Language'],
];

const dash = (v: number | null | undefined) => (v == null ? '—' : String(v));

function metricRows(m: SimulationMetrics): [string, React.ReactNode][] {
  return [
    [
      'Known answers recalled',
      m.truthFields
        ? `${m.recalled} of ${m.truthFields} (${Math.round(
            (m.recallRate ?? 0) * 100
          )}%)`
        : 'source has no answers',
    ],
    ['Answers beyond the facts', m.capturedBeyondFacts],
    ['Declined', m.declined],
    ['Turns to first card', dash(m.turnsToFirstCard)],
    ['Cards shown', m.cards],
    ['Turns to 50%', dash(m.turnsTo50)],
    ['Final completeness', `${m.finalPercentage}%`],
    ['Field writes', m.fieldWrites],
    ['Guidance calls', m.guidanceCalls],
    [
      'Groups fetched twice',
      m.repeatedGuidanceGroups.length
        ? m.repeatedGuidanceGroups.join(', ')
        : 'none',
    ],
    ['Avg chars (user / Hong)', `${m.avgUserChars} / ${m.avgHongChars}`],
    ['Failed turns', m.failedTurns],
  ];
}

const scoreColor = (n: number) => (n >= 4 ? 'green' : n >= 3 ? 'amber' : 'red');

interface SimulationBarProps {
  simulation: Simulation;
  /** Live turn count from socket events; the row lags while running. */
  liveTurn?: number;
  livePercentage?: number;
  stopping?: boolean;
  deleting?: boolean;
  onStop: () => void;
  onDelete: () => void;
  onClose: () => void;
}

/** Status, controls and (once finished) the scores of one simulation run. */
const SimulationBar = ({
  simulation: s,
  liveTurn,
  livePercentage,
  stopping,
  deleting,
  onStop,
  onDelete,
  onClose,
}: SimulationBarProps) => {
  const [color, label] = STATUS[s.status];
  const running = s.status === 'running';
  const judge = s.judge;
  const turn = Math.max(liveTurn ?? 0, s.turns);
  const percentage =
    livePercentage ?? s.progress[s.progress.length - 1]?.percentage;

  const headerRight = (
    <div className="row gap-2">
      {running ? (
        <Button
          size="sm"
          variant="secondary"
          leftIcon="x-circle"
          loading={stopping}
          onClick={onStop}
        >
          Stop
        </Button>
      ) : (
        <Button
          size="sm"
          variant="danger-ghost"
          leftIcon="trash"
          loading={deleting}
          onClick={onDelete}
        >
          Delete copy
        </Button>
      )}
      <Button
        size="sm"
        variant="ghost"
        iconOnly
        leftIcon="x"
        title="Close"
        onClick={onClose}
      />
    </div>
  );

  return (
    <Panel
      title="Simulation"
      icon="activity"
      headerRight={headerRight}
      scroll
      bodyStyle={{ maxHeight: 300 }}
    >
      <div className="col gap-3">
        <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
          <Badge color={color} dot>
            {label}
          </Badge>
          <span className="text-secondary" style={{ fontSize: 'var(--fs-sm)' }}>
            Turn {turn} of {s.maxTurns} · stops at {GOAL[s.goal]} ·{' '}
            {s.lang === 'cn' ? '简体中文' : 'English'}
            {percentage != null && ` · profile ${percentage}%`}
          </span>
          {running && <span className="spinner sm" />}
        </div>
        {!running && s.stopReason && (
          <span className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>
            Ended: {STOP_REASON[s.stopReason]}
            {s.error ? ` — ${s.error}` : ''}
          </span>
        )}

        {judge && (
          <div className="col gap-2">
            <div className="section-title">Judge</div>
            <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
              {JUDGE_LABELS.map(([key, name]) => (
                <Badge key={key} color={scoreColor(judge[key])} size="sm">
                  {name} {judge[key]}/5
                </Badge>
              ))}
            </div>
            {judge.issues.length > 0 && (
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 18,
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--text-secondary)',
                }}
              >
                {judge.issues.map((issue, i) => (
                  <li key={i}>
                    <span className="mono">Turn {issue.turn}</span> —{' '}
                    {issue.note}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {s.metrics && (
          <div className="col gap-2">
            <div className="section-title">Metrics</div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0,1fr) auto',
                columnGap: 16,
                rowGap: 4,
                fontSize: 'var(--fs-sm)',
              }}
            >
              {metricRows(s.metrics).map(([name, value]) => (
                <React.Fragment key={name}>
                  <span className="text-secondary">{name}</span>
                  <span className="mono" style={{ textAlign: 'right' }}>
                    {value}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
};

export default SimulationBar;
